import type * as mdast from 'mdast';

import type { Document, Fragment } from '@gitbook/slate';

import type { FormattingContext } from '../context';
import { gastToAst } from '../utils';
import { transformBlocks } from './transform-blocks';
import { transformInlines } from './transform-inlines';
import { transformText } from './transform-text';

/**
 * Convert a document to a mdast tree.
 */
export async function gastToMdast(
    document: Document | Fragment,
    context: FormattingContext
): Promise<mdast.Root> {
    const tree = await gastToAst<mdast.Root>(document, context, {
        transformBlocks,
        transformInlines,
        transformText,
    });

    if (document.isDocument()) {
        const footnotes = context.getFootnotes();
        if (footnotes.length > 0) {
            tree.children.push(...(await transformFootnotesToMdast(footnotes, context)));
        }
    }

    return tree;
}

/**
 * Transform the footnote definitions as a section with an ol at the bottom of the document.
 * It mimics the GitHub markdown rendering.
 */
async function transformFootnotesToMdast(
    footnotes: [string, Fragment][],
    context: FormattingContext
): Promise<mdast.FootnoteDefinition[]> {
    const result: mdast.FootnoteDefinition[] = [];

    for (const [footnoteId, footnoteFragment] of footnotes) {
        const root = await gastToMdast(footnoteFragment, context);

        result.push({
            type: 'footnoteDefinition',
            identifier: footnoteId,
            // @ts-ignore
            children: root.children,
        });
    }

    return result;
}
